import { fetchDataWithRetry } from "@/lib/Fetchers/fetchData";
import { channel } from "@/lib/Types/Channel";
import { BookingInfo } from "@/lib/Types/DateListResponse/BookingInfo";
import { PeakworkResponse } from "@/lib/Types/DateListResponse/Peakwork";
import { QuickVacancy } from "@/lib/Types/DateListResponse/QuickVacancy";
import { SpecialDealDetail } from "@/lib/Types/SpecialDealDetail";
import { sendKibanaLog } from "./sendKibanaLog";
import { AppConfig } from "@/lib/Types/AppConfig";

// Parse URL rooms string (e.g., "25,25,4,4" or "25,25;25,25,4") into API format
function parseRoomsForApi(
  roomsString: string,
): Array<{ adults: number[]; children: number[] }> {
  return roomsString.split(";").map((room) => {
    const ages = room.split(",").map(Number);
    return {
      adults: ages.filter((age) => age >= 18),
      children: ages.filter((age) => age < 18),
    };
  });
}

interface QuickVacancyPayload {
  cid: number | null;
  bookingInfo: BookingInfo;
  channel: string;
  clientId: boolean;
  departureDate: string;
  m: number;
  p: number;
  multiroom: boolean;
  peakworkResponse: PeakworkResponse | null | undefined;
  price: number;
  returnDate: string;
  toVoucherCodeRuleId: number | string | null;
  bookOnOptionRuleId: number | string | null;
  tourOperatorCode: string;
  rooms: Array<{ adults: number[]; children: number[] }>;
  travellers: string[];
  estimatedTotalPrice: number;
  type: string;
  isAutoVacancyChecked: boolean;
  device: string | undefined;
  departureAirport?: string;
  destinationAirport?: string;
  upsellingOffer?: boolean;
  skyscannerRedirectId?: string;
}

interface FetchQuickVacancyParams {
  searchParams: Record<string, string>;
  bookingInfo: BookingInfo;
  peakworkResponse: PeakworkResponse | null;
  departureAirport: string;
  destinationAirport: string;
  tourOperatorCode: string;
  price: number;
  hotelCid: number | null;
  estimatedTotalPrice: number;
  type?: "MANUAL" | "AUTO";
  specialDealsDetails?: SpecialDealDetail[];
  upsellingOffer?: boolean;
  appConfig: AppConfig;
}

export async function fetchQuickVacancy({
  searchParams,
  bookingInfo,
  peakworkResponse,
  departureAirport,
  destinationAirport,
  tourOperatorCode,
  price,
  hotelCid,
  estimatedTotalPrice,
  type = "MANUAL",
  specialDealsDetails,
  upsellingOffer,
  appConfig,
}: FetchQuickVacancyParams): Promise<QuickVacancy> {
  const params = searchParams;
  const { envConfig, viewMode, userData } = appConfig;

  // Extract toVoucherCodeRuleId from special deals
  const toVoucherDeal = specialDealsDetails?.find(
    (deal) => deal.specialDealTypes === "TO_VOUCHER",
  );
  const toVoucherCodeRuleId = toVoucherDeal?.ruleId ?? null;

  // Extract bookOnOptionRuleId from special deals. The backend uses it to decide
  // whether to check the offer as a plain availability check or as a book-on-option
  // check, so leaving it out makes the result unusable for book on option.
  const bookOnOptionDeal = specialDealsDetails?.find(
    (deal) => deal.specialDealTypes === "BOOK_ON_OPTION",
  );
  const bookOnOptionRuleId = bookOnOptionDeal?.ruleId ?? null;

  let payload: QuickVacancyPayload = {
    cid: hotelCid,
    bookingInfo,
    channel: viewMode.channel,
    clientId: false,
    departureDate: params.from,
    m: viewMode.mandator,
    p: viewMode.profile,
    multiroom: params.rooms.includes(";"),
    peakworkResponse,
    price,
    returnDate: params.to,
    toVoucherCodeRuleId,
    bookOnOptionRuleId,
    tourOperatorCode,
    rooms: parseRoomsForApi(params.rooms),
    travellers: params.rooms
      .split(";")
      .flatMap((room: string) => room.split(",")),
    estimatedTotalPrice,
    type,
    isAutoVacancyChecked: type === "AUTO",
    upsellingOffer: !!upsellingOffer,
    device: userData?.deviceType,

    ...(searchParams.skyscanner_redirectid && {
      skyscannerRedirectId: searchParams.skyscanner_redirectid,
    }),
  };

  if (viewMode.channel === channel.PACKAGE) {
    payload = {
      ...payload,
      departureAirport,
      destinationAirport,
    };
  }

  const baseUrl = "comvel-quickvacancy-service/rest/quickvacancy2";

  const url = `${envConfig.api}/${baseUrl}`;

  sendKibanaLog({
    envConfig,
    transactionId: searchParams?.transactionId,
    message: "QUICKVACANCY START",
    component: "quickvacancy",
    jsonMessage: payload,
    searchId: searchParams.searchId,
    category: "debug",
  });

  const data = await fetchDataWithRetry({
    url,
    method: "POST",
    params: payload,
    envConfig,
    service: "quickVacancy",
    transactionId: searchParams?.transactionId,
    searchId: searchParams.searchId,
  });

  sendKibanaLog({
    envConfig,
    transactionId: searchParams?.transactionId,
    message: `QUICKVACANCY ${data?.apiError ? "FAIL" : "SUCCESS"}`,
    component: "quickvacancy",
    jsonMessage: data?.apiError && data.message,
    searchId: searchParams.searchId,
    category: "debug",
  });

  return data;
}
